/*eslint-disable no-useless-concat */
import React from 'react';

import { List, Map } from 'immutable';

import { ImmutableMap, Optional } from './utils';
import * as game from './game';
import * as controls from './controls';

export type Level = ImmutableMap<{
  name: string,
  render_classes: Array<string>,
  flavor_text: React.ReactElement | string,
  level_help: (state: game.GameState) => Array<React.ReactElement | string>,
  initial_state: game.GameState,
  solution: Optional<List<game.Action>>,
}>;

const parse_direction = (direction: string): game.Direction => {
  switch (direction) {
    case 'u': return 'up';
    case 'd': return 'down';
    case 'l': return 'left';
    case 'r': return 'right';
    default: throw new Error(`Unknown direction: ${direction}`);
  }
}

const parse_solution = (sol: string): List<game.Action> => {
  let playerId = 0;
  let it = sol.split('').entries();
  let result = List<game.Action>();
  while (true) {
    let {value, done} = it.next();
    if (done) {
      break;
    }
    let c = value[1];
    switch (c) {
      case '(':
      case ')':
        break;
      case 'u':
      case 'd':
      case 'l':
      case 'r':
        result = result.push(Map({type: 'move', playerId, direction: parse_direction(c)}));
        break;
      case ' ':
        result = result.push(Map({type: 'wait'}));
        break;
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        playerId = parseInt(c, 10);
        break;
      case 'c': {
        let wormhole_id = parseInt(it.next().value[1]);
        let wormhole_out_id = parseInt(it.next().value[1]);
        let itemMap: {[key: string]: game.ItemType} = {'b': 'player', 's': 'shiny', 'r': 'rock'};
        let t: game.ItemType = itemMap[it.next().value[1] as string];
        let item = Map({type: t});
        let direction = parse_direction(it.next().value[1]);
        result = result.push(Map({type: 'enter', wormhole_id, wormhole_out_id, item: item, direction}));
        // let type = it.next().value[1];
        break;
      }
      default: {
        throw new Error(`Unknown character '${c}'`);
      }
    }
  }
  return result;
}

type SimpleWormholeState = ImmutableMap<{
  in: game.Location,
  out: game.Location,
  delta_t: number,
  outPushable?: boolean,
  default_direction?: Optional<game.Direction>,
}>

const parse_level = (
  name: string,
  flavor_text: React.ReactElement | string,
  level_help: Optional<(state: game.GameState) => Array<React.ReactElement | string>>,
  render_classes: Array<string>,
  gridstr: string,
  wormholes: Array<SimpleWormholeState>,
  events: Array<game.Event>,
  player_direction: Optional<game.Direction>,
  solution_str: string | null,
): Level => {
  let playerId = 0;
  gridstr = gridstr.trim();
  let grid = List<List<game.Cell>>();
  let lines = gridstr.trim().split('\n');
  let players = Map<number, game.PlayerState>();
  lines.forEach((line, y: number) => {
    let row = List<game.Cell>();
    Array.from(line).forEach((c: string, x: number) => {
      let cell = Map({
        items: List<game.Item>(),
      });
      switch (c) {
        case '#':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'exit'})));
          break;
        case 'x':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'wall'})));
          break;
        case 'o':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'rock'})));
          break;
        case 'e':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'egg'})));
          break;
        case 'h':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'hatching_egg'})));
          break;
        case 's':
        case '@':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'enter'})));
          if (c === 's') {
            cell = game.updateCell(cell, (items) => items.push(Map({type: 'eggshell'})));
          }
          // cell = game.updateCell(cell, (items) => items.push(Map({type: 'eggshell', flipped: true})));
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'player', id: playerId, facing: player_direction})));
          players = players.set(playerId, Map({
            location: Map({
              x, y,
            }),
            moved: false,
            age: 0,
          }));
          playerId++;
          break;
        case '$':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'shiny'})));
          break;
        case '/':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'twig'})));
          break;
        case 'k':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'key', facing: 'right'})));
          break;
        case 'd':
          cell = game.updateCell(cell, (items) => items.push(Map({type: 'door'})));
          break;
        case ' ':
          break;
        default:
          throw new Error(`Unknown character '${c}'`);
      }
      row = row.push(cell);
    });
    grid = grid.push(row);
  });
  if (lines.length <= 3 && lines[0].length <= 3) {
    render_classes.push('huge-cells');
  } else if (lines.length <= 7 && lines[0].length <= 7) {
    render_classes.push('big-cells');
  } else if (lines.length <= 11 && lines[0].length <= 11) {
    render_classes.push('medium-cells');
  }
  let wormholes_tup: Array<[number, game.WormholeState]> = [];
  let wormhole_id = 0;
  wormholes.forEach((wormhole: SimpleWormholeState) => {
    let location = wormhole.get('in');
    grid = game.updateGrid(grid, location, game.cellUpdater((items) => items.push(Map({
      type: 'wormholeIn',
      id: wormhole_id,
    }))));
    location = wormhole.get('out');
    grid = game.updateGrid(grid, location, game.cellUpdater((items) => items.push(Map({
        type: 'wormholeOut',
        id: wormhole_id,
        out_id: 0,
        pushable: wormhole.get('outPushable'),
        default_direction: wormhole.get('default_direction') || null,
        // active: true,
    }))));
    let wormhole_state: game.WormholeState = Map({
      in: wormhole.get('in'),
      outs: List<game.WormholeOutState>([
        Map({
          out_id: 0,
          base_t: 0,
          start_age: 0,
          delta_t: wormhole.get('delta_t'),
          location: wormhole.get('out'),
          nested_in: null,
        }),
      ]),
      outPushable: wormhole.get('outPushable'),
      singularity: false,
    });
    wormholes_tup.push([wormhole_id, wormhole_state]);
    wormhole_id++;
  });
  const gameState: game.GameState = Map({
    grid,
    events: List<game.Event>(events),
    wormholes: Map<number, game.WormholeState>(wormholes_tup),
    players,
    past: List<game.GameState>(),
    parallel_time: 0,
  });
  let solution: List<game.Action> | null = null;
  if (solution_str !== null) {
    solution = parse_solution(solution_str);
  }
  return Map({
    name,
    flavor_text,
    level_help: level_help === null ? (state: game.GameState) => [] : level_help,
    initial_state: gameState,
    render_classes,
    solution,
  });
}

function playerExit(time: Optional<number>): game.ExitEvent {
  if (time !== null) {
    throw new Error('make sure there are no time wormholes, this isnt really implemented sensibly.  uses the exit timeline')
  }
  return Map({
    type: 'exit',
    item: Map({type: 'player'}),
    time: time,
  })
}

function shinyExit(time: Optional<number>): game.ExitEvent {
  return Map({
    type: 'exit',
    item: Map({type: 'shiny'}),
    time: time,
  })
}

function eggExit(time: Optional<number>): game.ExitEvent {
  return Map({
    type: 'exit',
    item: Map({type: 'egg'}),
    time: time,
  })
}

function lay(age: number, player: number): game.ExitEvent {
  return Map({
    type: 'lay',
    player_id: player,
    age: age,
  })
}

function hatch(time: number): game.ExitEvent {
  return Map({
    type: 'hatch',
    time: time,
  })
}


export const LEVELS = [
  parse_level(
    'Hatch',
    `
The bird sees no mother.

But, she sees a beautiful gem!  It must be a hint to mother's whereabouts.
    `.trim(),
    (state) => {
      let help = [];
      // if (state.get('events').size === 1) {
      if (game.getTime(state) === 0) { 
        help.push(
          <React.Fragment>
                Use <kbd>{controls.wait.main}</kbd> to wait a tick
          </React.Fragment>
        )
      }
      return help;
    },
    [],
    `h$`,
    [], [
      hatch(4),
    ],
    'right',
    '     ',
  ),
  parse_level(
    'Early',
    `
Leaving the nest is hard, but starting each day early means more worms.

The bird always brings their precious gem with them, determined not to lose it.
    `.trim(),
    // 'Dawn',
    // 'A wind blows.  Entropy unfolds the nest, and shiny things scatter.',
    // `Yesterday is forgotten!
    //
    // An early start today means more worms for the bird to enjoy.
    //
    // The bird brings their precious egg with them, lest it be stolen.
    // `,
    (state) => {
      let help = [];
      if (!state.getIn(['players', 0, 'moved'])) {
        help.push(
          <React.Fragment>
            Use <kbd>&lt;arrows&gt;</kbd> or <kbd>w</kbd><kbd>a</kbd><kbd>s</kbd><kbd>d</kbd> to move
          </React.Fragment>
        )
      }
      // if (state.get('events').size === 3) {
      //   help.push(
      //     <React.Fragment>
      //           Use <kbd>{controls.wait.main}</kbd> to wait a tick
      //     </React.Fragment>
      //   )
      // }
      return help;
    },
    [],
    `
xxxxxxx
xxx xxx
xx   xx
xs$  #x
xx   xx
xxx xxx
xxxxxxx
    `, [], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    // 'rdrrudllurrr', // version with rock
    'rrrr',
  ),

  parse_level(
    'Space',
    `
Digging for worms, the bird sees a shiny hole in the ground and accidentally drops the gem into it.

Following the gem leads to disorientation and a change in scenery. She is thankful the gem is there.
    `.trim(),
    null,
    [],
    `
xxxxxxx
x   xxx
x   xxx
x    #x
xxx$xxx
xxx@xxx
xxxxxxx
    `, [
      Map({
        in: Map({x: 3, y: 1}),
        out: Map({x: 1, y: 3}),
        delta_t: 0,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'up',
    'uuulurrrrr'
  ),

  parse_level(
    'Rock',
    `
Rocks!  They resemble the gem, but without the shine and beauty.  They could be a clue as to the gem, which could be a clue about mother.

The bird pushes the boulders back repeatedly to clear the path forward.
     `.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxxx xxxxx
x@$oooo  #x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 5, y: 3}),
        delta_t: 0,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'rdrrruldrrrruldlluldrrruldluldrr'
    + 'dlllllurrdrruldllurrrrrrr'
  ),


  parse_level(
    'Time',
    `
The bird passes through another wormhole, and the sun jumps to the west. The gem gleams even more brightly.
   `.trim(),
// The bird is having fun, when time flies.
    (state) => {
      let help = []
      console.log(state.get('players').toJS())
      if (state.get('players').filter((pstate) => pstate.get('location') !== null).size === 0) {
        help.push(
          <React.Fragment>
              Use <kbd>{controls.wait.main}</kbd> to wait a tick.
          </React.Fragment>
        );
      }
      return help;
    },
    [],
    `
xxxxxxx
x   xxx
x x xxx
x    #x
xxx$xxx
xxx@xxx
xxxxxxx
    `, [
      Map({
        in: Map({x: 3, y: 1}),
        out: Map({x: 1, y: 3}),
        delta_t: 4, // could be 3
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'up',
    'uuudlluurr    rrrr'
  ),

  parse_level(
    'Push',
    `
A landslide is triggered, and the gem is lost in the commotion.

But it is a lucky gem, and it pops back into sight.
     `.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxxx xxxxx
x@$ ooo  #x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 5, y: 2}),
        delta_t: 5,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    // 'rrdrrrrruldllllurdrudrruldllur     rrrr ',
    'rdrrrudrruldllllurrdrrrrul     lll  rrrrr'
  ),


  /*
  parse_level(
    'Leader',  // archetype?
    `
TODO
    `.trim(),
    null,
    [],
    `
xxxxxxx
xxx xxx
xxx xxx
x $  #x
xxx xxx
xxx@xxx
xxxxxxx
    `, [
      Map({
        in: Map({x: 3, y: 1}),
        out: Map({x: 1, y: 3}),
        delta_t: -2,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'up',
    '0uu(c00bu)uu1rrrr'
  ),
*/

  parse_level(
    'Double',
    `
The bird sees another bird stuck in a brief yet eternal loop.

The other bird helps with the gem, and seems happy in their short moment.  The bird is grateful, both for the help and for having a longer life.
      `.trim(),
    null,
    [],
    `
xxxxxxx
xxx xxx
xxx xxx
x    #x
xxx$xxx
xxx@xxx
xxxxxxx
    `, [
      Map({
        in: Map({x: 3, y: 1}),
        out: Map({x: 1, y: 3}),
        delta_t: -4,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'up',
    'u(c00bu)1rruu0urr'
  ),


  parse_level(
    'Emit',
    `
The bird sees their future self, seemingly exceeding the speed of flight! 

In awe, the bird is able to imitate the motions.  The sun jumps to the east.

The bird notices their past self watching studiously.
    `.trim(),
    (state) => {
      let help = []
      if (!state.get('created')) {
        help.push(
          <React.Fragment>
                Try using <kbd>{controls.borrow.main}</kbd> to trigger exit events.
          </React.Fragment>
        )
      } else {
        if (state.get('players').size > 1) {
          help.push(
            <React.Fragment>
                  Use <kbd>{controls.cycle_player.main}</kbd> to cycle birds.
            </React.Fragment>
          )
        }
        // help.push(
        //   <React.Fragment>
        //         Use <kbd>{controls.wait.main}</kbd> to wait a tick.
        //   </React.Fragment>
        // );
      }
      return help;
    },
    [],
    `
xxxxxxx
x   xxx
x x$xxx
x    #x
xxx xxx
xxx@xxx
xxxxxxx
    `, [
      Map({
        in: Map({x: 3, y: 1}),
        out: Map({x: 1, y: 3}),
        delta_t: -4,
        // default_direction: 'right',
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'up',
    // yang solution
    'uulluur(c00su)(c00br)1rru0r1drr'
    // orig solution
    // '(c00su) uuu(c00br)1uurr(c00bu)1rrr0u1r'
  ),

// EASIER VERSION OF PULL
//     `
// xxxxxxxxxxx
// xxxxx xxxxx
// x@ $   o #x
// x         x
// xxxxxxxxxxx
//     `, [
//       Map({
//         in: Map({x: 5, y: 1}),
//         out: Map({x: 5, y: 2}),
//         delta_t: -4,
//       }),
//     ], [
//       shinyExit(null),
//       playerExit(null),
//     ],
//     'right',
//     'rrrrdrrru'
//     + '(c00ru)lld'
//     + '(c00su)l  udlllurrrrrdrru'
//     ,


  parse_level(
    'Pull',
    `
The gem always finds its way back to the bird.  The bird remembers that she must find her way to mother.
     `.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxxx xxxxx
x@$ o o  #x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 5, y: 2}),
        delta_t: -4,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'drrrrrrulldllllu'
    + '(c00su)rrr'
    + '(c00ru)dr urdrrru'
    ,
  ),


  parse_level(
    'Flock', /*Ducks',*/
    `
A group of birds flock together.  They seem on the same page, and fly in a tight formation to decrease drag while rotating so the leader doesn't tire.

Help, and be helped, in a virtuous cycle.
    `.trim(),
    /*'Suddenly there were many birds.  They flew in a tight formation, as working together was the best way to decrease drag.',*/
    null,
    [],
    `
xxxxxx
x xxxx
x  $#x
x x@ x
xxxxxx
    `, [
      Map({
        in: Map({x: 1, y: 1}),
        out: Map({x: 1, y: 3}),
        delta_t: -4,
      }),
    ], [
      playerExit(null),
      shinyExit(null),
    ],
    'up',
    'ru(c00bu)(c00bu)1r'
    + '(c00bu)(c00bu)(c00bu)1r'
    + '(c00bu)(c00bu)(c00bu)1l'
    + '(c00bu)(c00bu)3u1l4uu'
// 1rr0  ulld'
//     + '(c00bd)(c00bd)(c00bd)(c00bd)' + '3rdd'
//     + '(c00bd)(c00bd)(c00bd)(c00bd)(c00bd)' + '3rdd'
//     + '(c00bd)(c00bd)(c00bd)(c00bd)(c00bd)' + '(c00sl)4r '
//     + '(c00bd)(c00bd)(c00bd)(c00bd)(c00bd)' + '3l4rl'
//     + '(c00bd)(c00bd)(c00bd)(c00bd)6d' + '3uul2dddd'
  ),

// EASIER VERSION OF SUNKEN
//     `
// xxxxxxxxxxx
// xxxxx xxxxx
// x@ $oooo #x
// x         x
// xxxxxxxxxxx
//     `, [
//     Map({
//       in: Map({x: 5, y: 1}),
//       out: Map({x: 5, y: 3}),
//       delta_t: 0,
//       outPushable: true,
//     }),
//   ], [
//     shinyExit(null),
//     playerExit(null),
//   ],
//   'right',
//   'rrrdrrrrllluuu',
// ),
  parse_level(
    'Sunken', /*The wormhole traverses time */
    `
Descending into caverns, the wormholes are atop boulders.  TODO
    `.trim(),
    (state) => {
      let help = [];
      // if (state.get('events').size === 1) {
      help.push(
        <React.Fragment>
              Wormhole exits with solid borders are pushable
        </React.Fragment>
      )
      return help;
    },
    [],
    `
xxxxxxxxxxx
xxxxx xxxxx
x@$oooo  #x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 5, y: 3}),
        delta_t: 0,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'drrrrudrluurlllllrruulurdrrruulrrrrrrrlllllllurrdrruuu',
    //'drrrrurlurlllruulurdrruulurdrrudllulldrrrrrrrllluuu',
  ),

  parse_level(
    'Flatten', /*The wormhole traverses time */
    `
To kill time, the bird drops rocks into the ground.

An interesting pattern of lights, and then everything is still.
    `.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxxx xxxxx
x@$  o   #x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 6, y: 2}),
        delta_t: 0,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'rrdrrrruldludlllurdrrudllurrrrrr',
  ),

  parse_level(
    'Stretch', /*'The wormhole traverses spacetime',*/
    `
A hole goes through a hole already in a hole... 

The bird stares into the resulting abyss, and sees no sign of the rocks they dropped into it.
    `.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxxx xxxxx
x@$ooo   #x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 6, y: 2}),
        delta_t: 1,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'drrrrudrruldludllllurdrrrudlllurdrrudllurrrrrr',
  ),

/*
  parse_level(
    'Warp',
    `
The bird finds itself lost in the maze that is space.
    `.trim(),
    null,
    [],
    `
xxxxxxxxx
x @     x
x $ xx# x
xxx xxx x
x       x
x   xxx x
xxxxxxxxx
    `, [
      Map({
        in: Map({x: 7, y: 5}),
        out: Map({x: 2, y: 4}),
        // delta_t: 5,
        delta_t: 0,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'rddddllurdruuuurlrrrrddddldrurdd'
    + 'uurrrrdddlllldllurrrrr'
    + 'llluuurrrrddd'
    + 'lllluullurrrrlldddrrrrdd'
  ),

  parse_level(
    'Fold',
    `
    `.trim(),
    null,
    [],
    `
xxxxxxxxx
x @     x
x $ xx# x
xxx xxx x
x       x
x   xx xx
xxxxxxxxx
    `, [
      Map({
        in: Map({x: 6, y: 5}),
        out: Map({x: 2, y: 4}),
        delta_t: -8,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'ldrurdduurrrrdddd'
     + '(c00bl)1rdllurd' + '0l' + '1ruuuurrrrdddll'
     + '(c00bd)0dddl' + '1lrrd' + '0druuulurrrll'
     + '(c00bd)1d' + '0dddrrrd'
  ),
*/

  parse_level(
    'Nest', // 'The bird weaves spacetime',
    `
The bird experiments with putting a wormhole into another wormhole.
    `.trim(),
    null,
    [],
    `
xxxxxxx
xx   xx
xx x$xx
xx   #x
xxx xxx
x@   xx
xx   xx
xx   xx
xxxxxxx
    `, [
      Map({
        in: Map({x: 2, y: 7}),
        out: Map({x: 3, y: 3}),
        delta_t: 2,
        outPushable: false,
      }),
      Map({
        in: Map({x: 4, y: 1}),
        out: Map({x: 3, y: 5}),
        delta_t: 0,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'rdd  dddrdluld  dddrdluuuurudlluurr    rrr',
  ),

  parse_level(
    'Corner', // 'The bird weaves spacetime',
    `
TODO
    `.trim(),
    null,
    [],
    `
xxxxxxx
x    xx
x xx xx
x x#  x
x xxxxx
x     x
x  o  x
x  o  x
x  $  x
x  @  x
xxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 5}),
        out: Map({x: 4, y: 6}),
        delta_t: 0,
        outPushable: true,
      }),
      Map({
        in: Map({x: 1, y: 9}),
        out: Map({x: 2, y: 8}),
        delta_t: 1,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'up',
    'rruuuuudddrdllll uldd rdluuldd ddrdlrrruuuu    lurrrr    urrddrdluluulrrr    '
    + 'rrdruu    lurrrddllrrdllrrrdlluluuruldluuuudddrrrr    rrlldddddrddrdluuuldduuuuuuurrrddr    ll'
  ),

/*
  parse_level(
    'Nest 2 (WIP)', // 'The bird weaves spacetime',
    `
TODO
    `.trim(),
    null,
    [],
    `
xxxxxxx
xx   xx
xx x xx
xx   #x
xxx xxx
x@   xx
xx   xx
xx  $xx
xxxxxxx
    `, [
      Map({
        in: Map({x: 2, y: 7}),
        out: Map({x: 3, y: 3}),
        delta_t: -2,
        outPushable: false,
      }),
      Map({
        in: Map({x: 4, y: 1}),
        out: Map({x: 3, y: 6}),
        delta_t: 0,
        outPushable: true,
      }),
    ], [
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    null,
  ),
*/

  parse_level(
    'Lay', // simple version
    `
The bird lays an egg.  Something more precious than the gem!  What a wonderful day.
    `.trim(),
    null,
    [],
    `
xxxxxxxxx
xxx xx xx
x@$  o #x
x x xxoxx
x      xx
xxxxxxxxx
    `, [
    ], [
      lay(2, 0),
      eggExit(null),
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    'rrddrrruullrrddllludlluurrrrrr',
  ),
/*
  parse_level(
    'Lay WIP',
    `
TODO
    `.trim(),
    null,
    [],
    `
xxxxxxxxxxx
x     xx xx
x@$   xo #x
x     xxoxx
x        xx
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 3, y: 2}),
        out: Map({x: 7, y: 2}),
        delta_t: 0,
        outPushable: false,
      }),
      Map({
        in: Map({x: 5, y: 1}),
        out: Map({x: 3, y: 3}),
        delta_t: 0,
        outPushable: true,
      }),
    ], [
      lay(5, 0),
      eggExit(null),
      shinyExit(null),
      playerExit(null),
    ],
    'right',
    null,
  ),
*/

  parse_level(
    'Mother',
    `
The bird sees no way to stay united with their egg.

They choose to take their egg to safety before cavern caves in.  The bird is able to send the gem with the egg, hoping the child survives and benefits from the gem's luck.
    `.trim(),
    null,
    [],
    `
xxxxxxxx
x#xx   x
x  xo  x
x  x   x
xoxxo  x
x  x   x
xx  x  x
x@e$   x
x      x
xxxxxxxx
    `, [
      Map({
        in: Map({x: 4, y: 3}),
        out: Map({x: 5, y: 4}),
        delta_t: 0,
        outPushable: true,
      }),
      Map({
        in: Map({x: 3, y: 6}),
        out: Map({x: 2, y: 3}),
        delta_t: 0,
        outPushable: false,
      }),
    ], [
      eggExit(null),
      shinyExit(null),
    ],
    'right',
    'rrdrruuuurulruuldrddlddddllurdruuu'
    + 'ludrrulruuulld'
    // egg into wh1
    + 'urdddrdddlllrruulu'
    // wh1 out into wh2
    + 'drruuuuldddrdddlldlu'
    // shiny left wh1
    + 'rruuruul'
    // rock up wh2
    + 'rdddddlllllurdru'
    // rock left wh1
    + 'rruuurul'
    // push final rock
    + 'ddddllluuluu'
  ),


  /*
  parse_level(
    'The bird borrows spacetime',
    ''.trim(),
    null,
    [],
    `
xxxxxxxxx
x       x
x       x
x  xx # x
x@x     x
x   $xxxx
xxxo xxxx
xxx xxxxx
xxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 4}),
        out: Map({x: 4, y: 3}),
        delta_t: -2,
        outPushable: true,
      }),
      Map({
        in: Map({x: 3, y: 7}),
        out: Map({x: 3, y: 4}),
        delta_t: 5,
        outPushable: false,
      }),
      // Map({
      //   in: Map({x: 5, y: 1}),
      //   out: Map({x: 4, y: 5}),
      //   delta_t: 0,
      //   outPushable: true,
      // }),
    ], [
      playerExit(null),
      shinyExit(null),
    ],
    'right',
    null,
  ),

  parse_level(
    'The bird kills two with one stone',
    ''.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxxxxxxxxx
xxxxxxxxxxx
x    xx   x
x    xx   x
x@k$ dd  #x
x    xx   x
x    xx   x
xxxxxxxxxxx
xxxxxxxxxxx
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 5, y: 4}),
        out: Map({x: 5, y: 5}),
        delta_t: 2,
        outPushable: true,
      }),
      Map({
        in: Map({x: 2, y: 4}),
        out: Map({x: 2, y: 6}),
        delta_t: -8,
        outPushable: true,
      }),
    ], [
      playerExit(null),
      shinyExit(null),
    ],
    'right',
    'rrdrrudrrruldlludrruldludllurrrrrr',
  ),


  parse_level(
    'Two stones with one bird',
    'TODO'.trim(),
    null,
    [],
    `
xxxxxxxxxxx
x@   x   #x
x  o x o  x
x    x    x
x    x    x
xxxdxxxdxxx
x         x
x         x
x    $    x
x         x
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 7, y: 3}),
        out: Map({x: 3, y: 3}),
        delta_t: -4,
      }),
      Map({
        in: Map({x: 2, y: 3}),
        out: Map({x: 8, y: 3}),
        delta_t: -4,
      }),
    ], [
      playerExit(null),
      shinyExit(null),
    ],
    'right',
    null,
  ),

  parse_level(
    'The bird is swallowed',
    'TODO flavor text.'.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxx   xxxx
xxx     xxx
xx       xx
x         x
x@   $   #x
x         x
xx       xx
xxx     xxx
xxxx   xxxx
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 2, y: 5}),
        out: Map({x: 7, y: 5}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 3, y: 4}),
        out: Map({x: 6, y: 5}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 4, y: 3}),
        out: Map({x: 6, y: 6}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 5, y: 2}),
        out: Map({x: 5, y: 7}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 6, y: 3}),
        out: Map({x: 5, y: 6}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 7, y: 4}),
        out: Map({x: 4, y: 6}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 8, y: 5}),
        out: Map({x: 3, y: 5}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 7, y: 6}),
        out: Map({x: 4, y: 5}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 6, y: 7}),
        out: Map({x: 4, y: 4}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 5, y: 8}),
        out: Map({x: 5, y: 3}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 4, y: 7}),
        out: Map({x: 5, y: 4}),
        delta_t: 5,
      }),
      Map({
        in: Map({x: 3, y: 6}),
        out: Map({x: 6, y: 4}),
        delta_t: 5,
      }),
    ], [
      playerExit(null),
      shinyExit(null),
    ],
    'right',
    null
  ),

  parse_level(
    'The bird is swallowed',
    'TODO flavor text.'.trim(),
    null,
    [],
    `
xxxxxxxxxxx
xxxx   xxxx
xxx     xxx
xx       xx
x         x
x@   $   #x
x         x
xx       xx
xxx     xxx
xxxx   xxxx
xxxxxxxxxxx
    `, [
      Map({
        in: Map({x: 2, y: 5}),
        out: Map({x: 7, y: 5}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 3, y: 4}),
        out: Map({x: 6, y: 5}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 4, y: 3}),
        out: Map({x: 6, y: 6}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 5, y: 2}),
        out: Map({x: 5, y: 7}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 6, y: 3}),
        out: Map({x: 5, y: 6}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 7, y: 4}),
        out: Map({x: 4, y: 6}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 8, y: 5}),
        out: Map({x: 3, y: 5}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 7, y: 6}),
        out: Map({x: 4, y: 5}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 6, y: 7}),
        out: Map({x: 4, y: 4}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 5, y: 8}),
        out: Map({x: 5, y: 3}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 4, y: 7}),
        out: Map({x: 5, y: 4}),
        delta_t: -5,
      }),
      Map({
        in: Map({x: 3, y: 6}),
        out: Map({x: 6, y: 4}),
        delta_t: -5,
      }),
    ], [
      playerExit(null),
      shinyExit(null),
    ],
    'right',
    null
  ),
  */

];

// The universe gives a hoot.
// Better never than late
// wild goose chase?
// penny wise / pound foolish
// put one egg in all your baskets
// two birds to kill one stone
// You can't make an omelet without breaking some eggs
// what came first, chicken or the egg?
// Good luck is the mother of diligence.
// don't count your chickens before they hatch

